/**
 * Error Message Service for Project Lighthouse
 * Enhanced error message handling with templates, localization, and context awareness
 */

import {
  MessageTemplateProcessor,
  MessageContext,
  ActionSuggestion,
  UserType
} from './errorMessageTemplates';

// =============================================================================
// Error Message Service Interface
// =============================================================================

export interface ErrorMessageConfig {
  locale?: string;
  userType?: UserType;
  includeActionSuggestions?: boolean;
  includeErrorCodes?: boolean;
  useContextAwareness?: boolean;
}

export interface ProcessedErrorMessage {
  message: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  category: string;
  actionSuggestions: ActionSuggestion[];
  userType: UserType;
  templateId?: string;
  timestamp: Date;
}

// =============================================================================
// Error Code to Template Mapping
// =============================================================================

const ERROR_CODE_TO_TEMPLATE: Record<string, string> = {
  // Authentication errors
  'AUTH-001': 'AUTH_INVALID_CREDENTIALS',
  'AUTH-002': 'AUTH_ACCOUNT_LOCKED',
  'AUTH-003': 'AUTH_SESSION_EXPIRED',
  'AUTH-004': 'AUTH_PASSWORD_CHANGE_REQUIRED',

  // Authorization errors
  'AUTHZ-001': 'AUTHZ_ACCESS_DENIED',
  'AUTHZ-002': 'AUTHZ_ADMIN_REQUIRED',

  // Validation errors
  'VAL-001': 'VAL_REQUIRED_FIELDS',
  'VAL-002': 'VAL_INVALID_EMAIL',
  'VAL-003': 'VAL_PASSWORD_REQUIREMENTS',

  // Business logic errors
  'BIZ-001': 'BIZ_PATIENT_INACTIVE',
  'BIZ-002': 'BIZ_FORM_LOCKED',
  'BIZ-003': 'BIZ_SCHEDULE_CONFLICT',

  // System errors
  'DB-001': 'SYS_SERVICE_UNAVAILABLE',
  'DB-002': 'SYS_SERVICE_UNAVAILABLE',
  'FE-003': 'SYS_OFFLINE_MODE',
  'UNK-001': 'SYS_SERVICE_UNAVAILABLE', // Internal server error -> Service unavailable

  // HIPAA errors
  'HIPAA-001': 'HIPAA_ACCESS_DENIED',
  'HIPAA-003': 'HIPAA_CONSENT_REQUIRED',

  // Network errors
  'NETWORK_ERROR': 'SYS_CONNECTION_ERROR',
  'TIMEOUT_ERROR': 'SYS_CONNECTION_ERROR',
  'OFFLINE_ERROR': 'SYS_OFFLINE_MODE'
};

// =============================================================================
// Error Message Service Class
// =============================================================================

export class ErrorMessageService {
  private processor: MessageTemplateProcessor;
  private config: ErrorMessageConfig;
  private userContext: Partial<MessageContext>;

  constructor(config: ErrorMessageConfig = {}) {
    this.config = {
      locale: 'en',
      userType: 'employee',
      includeActionSuggestions: true,
      includeErrorCodes: false,
      useContextAwareness: true,
      ...config
    };

    this.processor = new MessageTemplateProcessor(this.config.locale);
    this.userContext = {
      userType: this.config.userType || 'employee'
    };
  }

  // =============================================================================
  // Public Methods
  // =============================================================================

  /**
   * Process an error into a user-friendly message
   */
  public processError(
    errorCode: string,
    context?: Partial<MessageContext>,
    variables?: Record<string, any>
  ): ProcessedErrorMessage {
    // Build complete context
    const messageContext: MessageContext = {
      userType: this.config.userType || 'employee',
      operation: 'unknown',
      timestamp: new Date(),
      ...this.userContext,
      ...context
    };

    // Get template ID from error code
    const templateId = this.getTemplateId(errorCode);
    
    // Process the message
    const message = this.processor.processMessage(templateId, messageContext, variables);
    
    // Get template metadata
    const template = this.processor.getTemplate(templateId);
    
    // Get action suggestions
    const actionSuggestions = this.config.includeActionSuggestions 
      ? this.processor.getActionSuggestions(templateId, messageContext)
      : [];

    return {
      message,
      severity: template?.severity || 'medium',
      category: template?.category || 'unknown',
      actionSuggestions,
      userType: messageContext.userType,
      templateId,
      timestamp: messageContext.timestamp!
    };
  }

  /**
   * Process API error response
   */
  public processAPIError(
    errorResponse: any,
    context?: Partial<MessageContext>
  ): ProcessedErrorMessage {
    let errorCode = 'UNKNOWN_ERROR';
    let variables: Record<string, any> = {};

    // Check if we have a user-friendly message directly from the API
    const directErrorMessage = errorResponse?.response?.data?.error || 
                              errorResponse?.response?.data?.message ||
                              errorResponse?.data?.error ||
                              errorResponse?.data?.message;

    // For authentication errors, preserve the original API message if it exists and is user-friendly
    if (directErrorMessage && 
        errorResponse?.response?.status === 401 && 
        typeof directErrorMessage === 'string' &&
        directErrorMessage.length < 200) { // Reasonable length check
      return {
        message: directErrorMessage,
        severity: 'medium' as const,
        category: 'authentication',
        actionSuggestions: [
          { label: 'Try Again', action: 'retry', primary: true },
          { label: 'Forgot Password?', action: 'reset_password' }
        ],
        userType: this.config.userType || 'employee',
        templateId: 'AUTH_DIRECT_MESSAGE',
        timestamp: new Date()
      };
    }

    // For validation/business logic errors (400), preserve the original API message if it exists and is user-friendly
    if (directErrorMessage && 
        errorResponse?.response?.status === 400 && 
        typeof directErrorMessage === 'string' &&
        directErrorMessage.length < 500 && // Allow longer messages for business logic explanations
        directErrorMessage.includes('.')) { // Check if it's a proper sentence (has period)
      return {
        message: directErrorMessage,
        severity: 'medium' as const,
        category: 'validation',
        actionSuggestions: [
          { label: 'Try Again', action: 'retry', primary: true },
          { label: 'Go Back', action: 'cancel' }
        ],
        userType: this.config.userType || 'employee',
        templateId: 'VALIDATION_DIRECT_MESSAGE',
        timestamp: new Date()
      };
    }

    // For server errors (500), preserve the original API message if available
    if (directErrorMessage && 
        errorResponse?.response?.status === 500 && 
        typeof directErrorMessage === 'string' &&
        directErrorMessage.length < 500) {
      return {
        message: directErrorMessage,
        severity: 'high' as const,
        category: 'system',
        actionSuggestions: [
          { label: 'Try Again', action: 'retry', primary: true },
          { label: 'Go Back', action: 'cancel' }
        ],
        userType: this.config.userType || 'employee',
        templateId: 'SERVER_ERROR_DIRECT_MESSAGE',
        timestamp: new Date()
      };
    }

    // Extract error information from different response formats for template-based processing
    if (errorResponse?.code) {
      errorCode = errorResponse.code;
    } else if (errorResponse?.response?.data?.code) {
      errorCode = errorResponse.response.data.code;
    } else if (errorResponse?.response?.status) {
      errorCode = this.mapHttpStatusToErrorCode(errorResponse.response.status);
    }

    // Extract variables from error context
    if (errorResponse?.context) {
      variables = errorResponse.context;
    }

    // Determine operation context from request
    const operationContext = this.determineOperationContext(errorResponse, context);

    return this.processError(errorCode, operationContext, variables);
  }

  /**
   * Process form validation errors
   */
  public processValidationErrors(
    validationErrors: Record<string, string | string[]>,
    context?: Partial<MessageContext>
  ): ProcessedErrorMessage[] {
    const results: ProcessedErrorMessage[] = [];

    Object.entries(validationErrors).forEach(([field, errors]) => {
      const errorArray = Array.isArray(errors) ? errors : [errors];
      
      errorArray.forEach(error => {
        const templateId = this.getValidationTemplateId(field, error);
        const variables = { field, value: error };
        
        const result = this.processError(templateId, {
          ...context,
          operation: 'form_validation',
          variables
        }, variables);

        results.push(result);
      });
    });

    return results;
  }

  /**
   * Get user-friendly message for error code
   */
  public getUserMessage(
    errorCode: string,
    context?: Partial<MessageContext>,
    variables?: Record<string, any>
  ): string {
    const result = this.processError(errorCode, context, variables);
    return result.message;
  }

  /**
   * Get action suggestions for error code
   */
  public getActionSuggestions(
    errorCode: string,
    context?: Partial<MessageContext>
  ): ActionSuggestion[] {
    const templateId = this.getTemplateId(errorCode);
    const messageContext: MessageContext = {
      userType: this.config.userType || 'employee',
      operation: 'unknown',
      ...this.userContext,
      ...context
    };

    return this.processor.getActionSuggestions(templateId, messageContext);
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<ErrorMessageConfig>): void {
    this.config = { ...this.config, ...config };
    
    if (config.locale) {
      this.processor.setLocale(config.locale);
    }
    
    if (config.userType) {
      this.userContext.userType = config.userType;
    }
  }

  /**
   * Set user context for personalized messages
   */
  public setUserContext(context: Partial<MessageContext>): void {
    this.userContext = { ...this.userContext, ...context };
  }

  /**
   * Get available locales
   */
  public getAvailableLocales(): string[] {
    return ['en', 'es']; // Extend as more locales are added
  }

  /**
   * Check if a template exists for error code
   */
  public hasTemplate(errorCode: string): boolean {
    const templateId = this.getTemplateId(errorCode);
    return this.processor.getTemplate(templateId) !== null;
  }

  // =============================================================================
  // Private Helper Methods
  // =============================================================================

  private getTemplateId(errorCode: string): string {
    // Direct mapping from error code to template
    if (ERROR_CODE_TO_TEMPLATE[errorCode]) {
      return ERROR_CODE_TO_TEMPLATE[errorCode];
    }

    // Pattern-based mapping for generic errors
    if (errorCode.startsWith('AUTH-')) {
      return 'AUTH_INVALID_CREDENTIALS'; // Default auth error
    } else if (errorCode.startsWith('AUTHZ-')) {
      return 'AUTHZ_ACCESS_DENIED'; // Default authz error
    } else if (errorCode.startsWith('VAL-')) {
      return 'VAL_REQUIRED_FIELDS'; // Default validation error
    } else if (errorCode.startsWith('BIZ-')) {
      return 'BIZ_PATIENT_INACTIVE'; // Default business logic error
    } else if (errorCode.startsWith('HIPAA-')) {
      return 'HIPAA_ACCESS_DENIED'; // Default HIPAA error
    } else if (errorCode.startsWith('DB-') || errorCode.startsWith('PERF-')) {
      return 'SYS_SERVICE_UNAVAILABLE'; // Default system error
    }

    // Fallback to connection error for unknown codes
    return 'SYS_CONNECTION_ERROR';
  }

  private getValidationTemplateId(field: string, error: string): string {
    // Map common validation errors to templates
    if (error.toLowerCase().includes('required')) {
      return 'VAL_REQUIRED_FIELDS';
    } else if (field.toLowerCase().includes('email') || error.toLowerCase().includes('email')) {
      return 'VAL_INVALID_EMAIL';
    } else if (field.toLowerCase().includes('password') || error.toLowerCase().includes('password')) {
      return 'VAL_PASSWORD_REQUIREMENTS';
    }

    return 'VAL_REQUIRED_FIELDS'; // Default validation error
  }

  private mapHttpStatusToErrorCode(status: number): string {
    const statusMap: Record<number, string> = {
      400: 'VAL-001', // Bad Request -> Validation Error
      401: 'AUTH-001', // Unauthorized -> Invalid Credentials
      403: 'AUTHZ-001', // Forbidden -> Access Denied
      404: 'DB-003', // Not Found -> Record Not Found
      409: 'DB-004', // Conflict -> Duplicate Record
      422: 'VAL-001', // Unprocessable Entity -> Validation Error
      423: 'AUTH-002', // Locked -> Account Locked
      429: 'SEC-002', // Too Many Requests -> Rate Limited
      500: 'UNK-001', // Internal Server Error -> Unknown Error
      502: 'SYS_SERVICE_UNAVAILABLE', // Bad Gateway -> Service Unavailable
      503: 'SYS_SERVICE_UNAVAILABLE', // Service Unavailable
      504: 'SYS_CONNECTION_ERROR' // Gateway Timeout -> Connection Error
    };

    return statusMap[status] || 'UNK-001';
  }

  private determineOperationContext(
    errorResponse: any,
    context?: Partial<MessageContext>
  ): Partial<MessageContext> {
    let operation = 'unknown';

    // Try to determine operation from request URL or method
    const request = errorResponse?.config || errorResponse?.request;
    if (request?.url) {
      if (request.url.includes('/auth/')) {
        operation = 'authentication';
      } else if (request.url.includes('/patients/')) {
        operation = 'patient_management';
      } else if (request.url.includes('/employees/')) {
        operation = 'employee_management';
      } else if (request.url.includes('/supervisions/')) {
        operation = 'supervision_management';
      }
    }

    // Override with explicit context if provided
    return {
      operation,
      ...context
    };
  }
}

// =============================================================================
// Create Default Instance
// =============================================================================

export const errorMessageService = new ErrorMessageService();

// =============================================================================
// Convenience Functions
// =============================================================================

export function getUserFriendlyMessage(
  errorCode: string,
  context?: Partial<MessageContext>,
  variables?: Record<string, any>
): string {
  return errorMessageService.getUserMessage(errorCode, context, variables);
}

export function processAPIErrorMessage(
  errorResponse: any,
  context?: Partial<MessageContext>
): ProcessedErrorMessage {
  return errorMessageService.processAPIError(errorResponse, context);
}

export function getErrorActionSuggestions(
  errorCode: string,
  context?: Partial<MessageContext>
): ActionSuggestion[] {
  return errorMessageService.getActionSuggestions(errorCode, context);
}

export function setUserLocale(locale: string): void {
  errorMessageService.updateConfig({ locale });
}

export function setUserType(userType: UserType): void {
  errorMessageService.updateConfig({ userType });
}

export default ErrorMessageService;